import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  Stack,
  Typography
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import { FEATURE_FLAG_SKIP_ENLIGHTEN_PRO_LICENSE_CHECK } from "../../../../common/features";
import { useFeatureFlagEnabled } from "../../../../common/posthog";
import { ConfirmationDialog } from "../../../../components/ui/ConfirmationDialog";
import { NotificationSnackbar } from "../../../../components/ui/NotificationSnackbar";
import { logError } from "../../../../utils/error";
import { useAuth } from "../../../auth/AuthContext";
import { LICENSE_UNAVAILABLE_MESSAGE } from "../../constants/license";
import { useNotification } from "../../hooks/useNotification";
import { useUserLicenses } from "../../hooks/useUserLicenses";
import { useAccountAnalysisStore } from "../../store/accountAnalysisStore";
import { AnalysisEntity } from "../../types";
import { parseSupabaseError } from "../../utils/errorHandling";
import { BulkAddPayload, BulkAddPeopleDialog } from "./BulkAddPeopleDialog";
import { EntityBulkActions } from "./EntityBulkActions";
import { EntityDialog } from "./EntityDialog";
import { EntityFilterBar } from "./EntityFilterBar";
import { EntityTable } from "./EntityTable";

export const AnalysisEntities: React.FC = () => {
  const { tenantId } = useAuth();
  const skipLicenseCheck = useFeatureFlagEnabled(FEATURE_FLAG_SKIP_ENLIGHTEN_PRO_LICENSE_CHECK);
  const {
    entities,
    profiles,
    selectedEntityIds,
    setSelectedEntityIds,
    toggleEntitySelection,
    clearEntitySelection,
    upsertEntity,
    upsertEntitiesBulk,
    fetchAllEmails,
    updateEntity,
    deleteEntity
  } = useAccountAnalysisStore();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<AnalysisEntity | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  }>({
    open: false,
    title: "",
    message: "",
    onConfirm: async () => {}
  });
  const [selectedStatus, setSelectedStatus] = useState("");
  const [allEmails, setAllEmails] = useState<{ email: string; is_deleted: boolean }[]>([]);
  const { notification, showNotification, hideNotification } = useNotification();

  useEffect(() => {
    const loadAllEmails = async () => {
      try {
        const emails = await fetchAllEmails();
        setAllEmails(emails);
      } catch (error) {
        logError("Failed to fetch all emails", error);
      }
    };
    loadAllEmails();
  }, [entities.length, fetchAllEmails]);

  const entityEmails = useMemo(
    () => (skipLicenseCheck ? [] : entities.map((e) => e.email)),
    [entities, skipLicenseCheck]
  );
  const {
    resultByEmail,
    isLoading: licensesLoading,
    error: licensesError
  } = useUserLicenses(entityEmails);

  const filteredEntities = useMemo(() => {
    return entities.filter((entity) => {
      const matchesSearch = entity.email
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesGroup =
        !selectedGroup ||
        (selectedGroup === "unassigned" && !entity.analysis_profile_id) ||
        entity.analysis_profile_id === selectedGroup;
      const matchesStatus = !selectedStatus || entity.status === selectedStatus;

      return matchesSearch && matchesGroup && matchesStatus;
    });
  }, [entities, searchTerm, selectedGroup, selectedStatus]);

  const handleAddEntity = () => {
    setEditingEntity(null);
    setDialogOpen(true);
  };

  const handleEditEntity = (entity: AnalysisEntity) => {
    setEditingEntity(entity);
    setDialogOpen(true);
  };

  const handleSaveEntity = async (entityData: Partial<AnalysisEntity>) => {
    try {
      if (editingEntity) {
        await updateEntity(editingEntity.id, entityData);
      } else {
        await upsertEntity({
          tenant_id: tenantId || "default-tenant",
          email: entityData.email!,
          analysis_profile_id: entityData.analysis_profile_id || null,
          status: entityData.status || "active"
        });
      }
      setDialogOpen(false);
      setEditingEntity(null);
    } catch (error) {
      logError("Failed to save entity", error);
      const errorMessage = parseSupabaseError(error);
      showNotification(errorMessage);
    }
  };

  const handleDeleteEntity = (id: string) => {
    const entity = entities.find((e) => e.id === id);
    if (!entity) {
      return;
    }

    setDeleteConfirmation({
      open: true,
      title: "Delete Person",
      message: `Are you sure you want to delete "${entity.email}"?`,
      onConfirm: async () => {
        try {
          await deleteEntity(id);
          setDeleteConfirmation(prev => ({ ...prev, open: false }));
        } catch (error) {
          logError("Failed to delete entity", error);
          const errorMessage = parseSupabaseError(error);
          showNotification(errorMessage);
        }
      }
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedEntityIds(filteredEntities.map((e) => e.id));
    } else {
      clearEntitySelection();
    }
  };

  const handleBulkEnable = async () => {
    try {
      await Promise.all(
        selectedEntityIds.map((id) => updateEntity(id, { status: "active" }))
      );
      clearEntitySelection();
    } catch (error) {
      logError("Failed to enable entities", error);
      const errorMessage = parseSupabaseError(error);
      showNotification(errorMessage);
    }
  };

  const handleBulkDisable = async () => {
    try {
      await Promise.all(
        selectedEntityIds.map((id) => updateEntity(id, { status: "disabled" }))
      );
      clearEntitySelection();
    } catch (error) {
      logError("Failed to disable entities", error);
      const errorMessage = parseSupabaseError(error);
      showNotification(errorMessage);
    }
  };

  const handleBulkAdd = async ({ emails, profileId, status, summary }: BulkAddPayload) => {
    const entitiesToAdd = emails.map((email) => ({
      tenant_id: tenantId || "default-tenant",
      email,
      analysis_profile_id: profileId,
      status
    }));

    try {
      await upsertEntitiesBulk(entitiesToAdd);
      const successPart = `Successfully added ${emails.length} ${emails.length === 1 ? "person" : "people"}`;
      const issueParts = [
        summary.skippedNoLicense.length > 0 && `Skipped ${summary.skippedNoLicense.length} without an Enlighten Pro license: ${summary.skippedNoLicense.join(", ")}`,
        summary.skippedNotFound.length > 0 && `Skipped ${summary.skippedNotFound.length} not found in Spokephone: ${summary.skippedNotFound.join(", ")}`,
        summary.failedLookup.length > 0 && `License check failed for ${summary.failedLookup.length}: ${summary.failedLookup.join(", ")}. Please retry these.`
      ].filter((part): part is string => Boolean(part));
      const message = [successPart, ...issueParts].join(" — ");
      showNotification(message, issueParts.length > 0 ? "error" : "success");
    } catch (error) {
      logError("Failed to bulk add entities", error);
      const errorMessage = parseSupabaseError(error);
      throw new Error(errorMessage);
    }
  };

  const handleBulkDelete = () => {
    const count = selectedEntityIds.length;
    setDeleteConfirmation({
      open: true,
      title: "Delete People",
      message: `Are you sure you want to delete ${count} ${count === 1 ? "person" : "people"}?`,
      onConfirm: async () => {
        try {
          await Promise.all(selectedEntityIds.map((id) => deleteEntity(id)));
          clearEntitySelection();
          setDeleteConfirmation(prev => ({ ...prev, open: false }));
        } catch (error) {
          logError("Failed to delete entities", error);
          const errorMessage = parseSupabaseError(error);
          showNotification(errorMessage);
        }
      }
    });
  };

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          People
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage people and their analysis group assignments
        </Typography>
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <EntityFilterBar
          searchTerm={searchTerm}
          selectedGroup={selectedGroup}
          selectedStatus={selectedStatus}
          profiles={profiles}
          onSearchChange={setSearchTerm}
          onGroupChange={setSelectedGroup}
          onStatusChange={setSelectedStatus}
        />

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setBulkDialogOpen(true)}
          >
            Bulk Upload
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddEntity}
          >
            Add Person
          </Button>
        </Stack>
      </Stack>

      <EntityBulkActions
        selectedCount={selectedEntityIds.length}
        onEnable={handleBulkEnable}
        onDisable={handleBulkDisable}
        onDelete={handleBulkDelete}
      />

      {!skipLicenseCheck && licensesError && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mb: 2 }}
        >
          {LICENSE_UNAVAILABLE_MESSAGE}
        </Alert>
      )}

      <EntityTable
        entities={filteredEntities}
        profiles={profiles}
        selectedIds={selectedEntityIds}
        showLicenseColumn={!skipLicenseCheck}
        licensesByEmail={resultByEmail}
        licensesLoading={licensesLoading}
        licensesFailed={Boolean(licensesError)}
        onSelectAll={handleSelectAll}
        onSelectEntity={toggleEntitySelection}
        onEditEntity={handleEditEntity}
        onDeleteEntity={handleDeleteEntity}
      />

      <NotificationSnackbar
        {...notification}
        onClose={hideNotification}
      />

      {dialogOpen && (
        <EntityDialog
          open
          entity={editingEntity}
          profiles={profiles}
          existingEmails={allEmails}
          onClose={() => setDialogOpen(false)}
          onSave={handleSaveEntity}
        />
      )}

      <ConfirmationDialog
        open={deleteConfirmation.open}
        title={deleteConfirmation.title}
        message={deleteConfirmation.message}
        onConfirm={deleteConfirmation.onConfirm}
        onCancel={() => setDeleteConfirmation(prev => ({ ...prev, open: false }))}
      />

      <BulkAddPeopleDialog
        open={bulkDialogOpen}
        profiles={profiles}
        existingEmails={allEmails}
        onClose={() => setBulkDialogOpen(false)}
        onSave={handleBulkAdd}
      />
    </Box>
  );
};
